import React from "react"
import { Field } from "lib/formik"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"

/**
 * Platform-specific advanced settings for banner slides.
 * `fields` maps logical keys → Formik field names (mobile vs web).
 */
const SlideAdvancedSettings = ({
  fields,
  values,
  errors,
  touched,
  setFieldValue,
  canUseGiftCardListLink,
  giftCardCategories,
  allCategories,
  itemsListDropdown,
  allItemsList,
  language,
}) => {
  const { t } = useTranslation()

  const linkType = values[fields.linkType]
  const category = values[fields.category]

  return (
    <>
      <div className="col-lg-6">
        <div className="form_field">
          <label htmlFor={fields.titleAr}>{t("Title(عربي)")}</label>
          <Field
            type="text"
            id={fields.titleAr}
            name={fields.titleAr}
            style={{ direction: "rtl" }}
            className={`form-control ${
              touched[fields.titleAr] && errors[fields.titleAr]
                ? "is-invalid"
                : ""
            }`}
            placeholder={t("Title(عربي)")}
          />
          <TextError name={fields.titleAr} />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form_field">
          <label htmlFor={fields.title}>{t("Title(English)")}</label>
          <Field
            type="text"
            id={fields.title}
            name={fields.title}
            className={`form-control ${
              touched[fields.title] && errors[fields.title] ? "is-invalid" : ""
            }`}
            placeholder={t("Title(English)")}
          />
          <TextError name={fields.title} />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form_field">
          <label htmlFor={fields.descriptionAr}>{t("Description(عربي)")}</label>
          <Field
            as="textarea"
            id={fields.descriptionAr}
            name={fields.descriptionAr}
            rows={1}
            placeholder={t("Description(عربي)")}
            style={{ direction: "rtl" }}
            className={`form-control ${
              touched[fields.descriptionAr] && errors[fields.descriptionAr]
                ? "is-invalid"
                : ""
            }`}
          />
          <TextError name={fields.descriptionAr} />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form_field">
          <label htmlFor={fields.description}>
            {t("Description(English)")}
          </label>
          <Field
            as="textarea"
            id={fields.description}
            name={fields.description}
            rows={1}
            placeholder={t("Description(English)")}
            className={`form-control ${
              touched[fields.description] && errors[fields.description]
                ? "is-invalid"
                : ""
            }`}
          />
          <TextError name={fields.description} />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form_field">
          <label htmlFor={fields.linkLabelAr}>{t("Link Label(عربي)")}</label>
          <Field
            type="text"
            id={fields.linkLabelAr}
            placeholder={t("Link Label(عربي)")}
            name={fields.linkLabelAr}
            style={{ direction: "rtl" }}
            className={`form-control ${
              touched[fields.linkLabelAr] && errors[fields.linkLabelAr]
                ? "is-invalid"
                : ""
            }`}
          />
          <TextError name={fields.linkLabelAr} />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form_field">
          <label htmlFor={fields.linkLabel}>{t("Link Label(English)")}</label>
          <Field
            type="text"
            id={fields.linkLabel}
            name={fields.linkLabel}
            placeholder={t("Link Label(English)")}
            className="form-control"
          />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form_field">
          <label htmlFor={fields.linkType}>{t("Link this banner to")}</label>
          <Field
            as="select"
            id={fields.linkType}
            name={fields.linkType}
            onChange={e => {
              const selectedValue = e.target.value
              setFieldValue(fields.linkType, selectedValue)
              setFieldValue(fields.item, "")
              setFieldValue(fields.category, "")
              setFieldValue(fields.linkValue, "")
              setFieldValue(fields.externalLink, "")
            }}
          >
            <option>{t("Select Options")}</option>
            <option value="CATEGORY">{t("Category")}</option>
            <option value="ITEM">{t("Item")}</option>
            {canUseGiftCardListLink && (
              <option value="GIFT_CARDS_LIST_VIEW">{t("Gift Card List")}</option>
            )}
            <option value="EXTERNAL_LINK">{t("External Link")}</option>
          </Field>
        </div>
      </div>

      {canUseGiftCardListLink && linkType === "GIFT_CARDS_LIST_VIEW" && (
        <div className="col-lg-6">
          <div className="form_field">
            <label htmlFor={fields.linkValue}>
              {t("Select Gift Card Category")}
            </label>
            <Field
              as="select"
              id={fields.linkValue}
              name={fields.linkValue}
              onChange={e => {
                setFieldValue(fields.linkValue, e.target.value)
              }}
            >
              <option hidden>{t("Select Category")}</option>
              {giftCardCategories &&
                giftCardCategories.map(categoryOption => (
                  <option
                    key={categoryOption.cardType}
                    value={categoryOption.cardType}
                  >
                    {categoryOption.cardType}
                  </option>
                ))}
            </Field>
          </div>
        </div>
      )}

      {linkType === "EXTERNAL_LINK" && (
        <div className="col-lg-6">
          <div className="form_field">
            <label htmlFor={fields.externalLink}>
              {t("External Link URL")} <span className="text-danger">*</span>
            </label>
            <Field
              type="url"
              id={fields.externalLink}
              name={fields.externalLink}
              placeholder="https://example.com"
              className="form-control"
            />
            <TextError name={fields.externalLink} />
          </div>
        </div>
      )}

      {(linkType === "CATEGORY" || linkType === "ITEM") && (
        <div className="col-lg-6">
          <div className="form_field">
            <label htmlFor={fields.category}>{t("Select Category")}</label>
            <Field
              as="select"
              id={fields.category}
              name={fields.category}
              onChange={e => {
                const { value } = e.target
                setFieldValue(fields.item, "")
                allItemsList(value)
                setFieldValue(fields.category, value)
              }}
            >
              <option hidden>{t("Select Category")}</option>
              {allCategories &&
                allCategories.map(option => (
                  <option key={option._id} value={option._id}>
                    {language === "sa" ? option.nameAr : option.name}
                  </option>
                ))}
            </Field>
          </div>
        </div>
      )}

      {category && linkType === "ITEM" && (
        <div className="col-lg-6">
          <div className="form_field">
            <label htmlFor={fields.item}>{t("Select Item")}</label>
            <Field as="select" id={fields.item} name={fields.item}>
              <option hidden>{t("Select Item")}</option>
              {itemsListDropdown &&
                itemsListDropdown.map(option => (
                  <option key={option._id} value={option._id}>
                    {language === "sa" ? option.nameAr : option.name}
                  </option>
                ))}
            </Field>
          </div>
        </div>
      )}

      <div className="col-lg-6">
        <div className="form_field">
          <label htmlFor={fields.label}>{t("Label")}</label>
          <Field
            type="text"
            id={fields.label}
            name={fields.label}
            placeholder={t("Label")}
            className="form-control"
          />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form_field">
          <label htmlFor={fields.duration}>
            {t("Duration (seconds)")}
          </label>
          <Field
            type="number"
            id={fields.duration}
            name={fields.duration}
            min={0}
            step={1}
            placeholder={t("Duration (seconds)")}
            className={`form-control ${
              touched[fields.duration] && errors[fields.duration]
                ? "is-invalid"
                : ""
            }`}
          />
          <TextError name={fields.duration} />
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form_field">
          <label htmlFor={fields.displayOrder}>{t("Display Order")}</label>
          <Field
            type="number"
            id={fields.displayOrder}
            name={fields.displayOrder}
            placeholder={t("Display Order")}
            className={`form-control ${
              touched[fields.displayOrder] && errors[fields.displayOrder]
                ? "is-invalid"
                : ""
            }`}
          />
          <TextError name={fields.displayOrder} />
        </div>
      </div>
    </>
  )
}

export const MOBILE_ADVANCED_FIELDS = {
  hasAdvancedSettings: "hasAdvancedSettings",
  title: "title",
  titleAr: "titleAr",
  description: "description",
  descriptionAr: "descriptionAr",
  linkLabel: "linkLabel",
  linkLabelAr: "linkLabelAr",
  linkType: "linkType",
  linkValue: "linkValue",
  externalLink: "externalLink",
  category: "category",
  item: "item",
  label: "label",
  duration: "duration",
  displayOrder: "displayOrder",
}

export const WEB_ADVANCED_FIELDS = {
  hasAdvancedSettings: "hasWebAdvancedSettings",
  title: "webTitle",
  titleAr: "webTitleAr",
  description: "webDescription",
  descriptionAr: "webDescriptionAr",
  linkLabel: "webLinkLabel",
  linkLabelAr: "webLinkLabelAr",
  linkType: "webLinkType",
  linkValue: "webLinkValue",
  externalLink: "webExternalLink",
  category: "webCategory",
  item: "webItem",
  label: "webLabel",
  duration: "webDuration",
  displayOrder: "webDisplayOrder",
}

export default SlideAdvancedSettings
